import { Typography } from "@mui/material";
import { type Currency } from "@prisma/client";
import classNames from "classnames";
import React, { useMemo } from "react";
import { useMoney } from "~/hooks/useMoney";
import { range } from "~/lib/lodash";

type Props = {
  currency: Currency;
  chartWidth: number;
  min: number;
  max: number;
  showFigures?: boolean;
};

const BASE_STEP_RANGE = 1000_00;
const MAX_STEPS_DISTANCE = 10;

export const MarketDataChartBackground: React.FC<Props> = ({ currency, chartWidth, min, max, showFigures = false }) => {
  const maxStepsCount = chartWidth / MAX_STEPS_DISTANCE;
  const money = useMoney(currency);

  const amplitude = max - min;

  const { stepRange, stepsCount } = useMemo(() => {
    let stepRange = BASE_STEP_RANGE;
    let stepsCount = Math.ceil(amplitude / stepRange);

    while (stepsCount > maxStepsCount) {
      stepRange = stepRange * 2;
      stepsCount = Math.ceil(amplitude / stepRange);
    }

    return { stepRange, stepsCount };
  }, [amplitude, maxStepsCount]);

  const steps = range(stepsCount).map((i) => min + (amplitude * i) / (amplitude / stepRange));

  const amountToPosition = (amount: number) => (amount - min) / amplitude;

  return (
    <div className="absolute inset-0">
      {steps.map((step, index) => {
        const position = amountToPosition(step);
        const offset = index === steps.length - 1 ? -1 : 0;
        const isMajorStep = index % 10 === 0;
        const shiftLatestAmount = index > steps.length - 4;

        return (
          <div key={step} className="absolute inset-y-0" style={{ left: `calc(${position * 100}% + ${offset}px)` }}>
            <div
              className={classNames("absolute top-0 bottom-0 w-px border-l border-dashed", {
                "-bottom-1.5 border-gray-300": isMajorStep,
                "border-gray-200": !isMajorStep,
              })}
            />

            {isMajorStep && showFigures && (
              <div
                className={classNames("absolute -bottom-6 -translate-x-1/2", {
                  "-translate-x-full": shiftLatestAmount,
                })}
              >
                <Typography variant="caption" color="text.secondary">
                  {money.format(step, { roundTo: 100 })}
                </Typography>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
